import { useState, useEffect, useCallback } from 'react';

interface OfflineData {
  key: string;
  data: any;
  timestamp: number;
  action: 'create' | 'update' | 'delete';
}

const OFFLINE_STORAGE_KEY = 'fitpro_offline_queue';

export const useOfflineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);
  const [pendingSync, setPendingSync] = useState(0);

  // Load pending sync count
  useEffect(() => {
    const stored = localStorage.getItem(OFFLINE_STORAGE_KEY);
    if (stored) {
      try {
        const queue: OfflineData[] = JSON.parse(stored);
        setPendingSync(queue.length);
      } catch {
        setPendingSync(0);
      }
    }
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setJustReconnected(true);
      
      // Auto-hide reconnected toast after 3s
      setTimeout(() => setJustReconnected(false), 3000);
      
      // Trigger sync
      syncOfflineData();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setJustReconnected(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Retry connection
  const retryConnection = useCallback(async () => {
    setIsReconnecting(true);
    
    try {
      // Ping to check connection
      const response = await fetch('/api/health', { 
        method: 'HEAD',
        cache: 'no-store' 
      }).catch(() => null);
      
      if (response?.ok || navigator.onLine) {
        setIsOnline(true);
        setJustReconnected(true);
        setTimeout(() => setJustReconnected(false), 3000);
      }
    } catch {
      // Still offline
    } finally {
      setIsReconnecting(false);
    }
  }, []);

  // Sync offline data
  const syncOfflineData = useCallback(async () => {
    const stored = localStorage.getItem(OFFLINE_STORAGE_KEY);
    if (!stored) return;

    try {
      const queue: OfflineData[] = JSON.parse(stored);
      if (queue.length === 0) return;

      // Process queue (implement actual sync logic based on your needs)
      console.log('Syncing offline data:', queue.length, 'items');
      
      // For now, just clear the queue
      // In a real implementation, you'd process each item
      localStorage.removeItem(OFFLINE_STORAGE_KEY);
      setPendingSync(0);
    } catch (error) {
      console.error('Error syncing offline data:', error);
    }
  }, []);

  // Queue data for sync when offline
  const queueForSync = useCallback((key: string, data: any, action: OfflineData['action']) => {
    const stored = localStorage.getItem(OFFLINE_STORAGE_KEY);
    const queue: OfflineData[] = stored ? JSON.parse(stored) : [];
    
    queue.push({
      key,
      data,
      timestamp: Date.now(),
      action,
    });

    localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(queue));
    setPendingSync(queue.length);
  }, []);

  return {
    isOnline,
    isReconnecting,
    justReconnected,
    pendingSync,
    retryConnection,
    queueForSync,
    syncOfflineData,
  };
};
